"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.InAppAgentQuestionnaireResultSchema = exports.InAppAgentQuestionnaireAnswersSchema = exports.InAppAgentQuestionnaireArgsSchema = exports.IN_APP_AGENT_QUESTIONNAIRE_MAX_ANSWER_LENGTH = exports.IN_APP_AGENT_QUESTIONNAIRE_MAX_OPTIONS = exports.IN_APP_AGENT_QUESTIONNAIRE_MIN_OPTIONS = exports.IN_APP_AGENT_QUESTIONNAIRE_MAX_QUESTIONS = void 0;
exports.createInAppAgentQuestionnaireAnswersSchema = createInAppAgentQuestionnaireAnswersSchema;
exports.resolveInAppAgentQuestionnaireAnswers = resolveInAppAgentQuestionnaireAnswers;
const zod_1 = __importDefault(require("zod"));
exports.IN_APP_AGENT_QUESTIONNAIRE_MAX_QUESTIONS = 4;
exports.IN_APP_AGENT_QUESTIONNAIRE_MIN_OPTIONS = 2;
exports.IN_APP_AGENT_QUESTIONNAIRE_MAX_OPTIONS = 4;
exports.IN_APP_AGENT_QUESTIONNAIRE_MAX_ANSWER_LENGTH = 2000;
// Every field is required (nullable instead of optional) so the schema stays
// valid for OpenAI strict tool calling. Questions and options are addressed by
// position, so the model never has to invent ids.
const InAppAgentQuestionnaireOptionSchema = zod_1.default.object({
    label: zod_1.default.string().min(1).max(200),
    description: zod_1.default
        .string()
        .max(500)
        .refine((description) => description.trim().length > 0, {
        message: "Give every option a description.",
    })
        .describe("One short sentence explaining the option."),
    recommended: zod_1.default
        .boolean()
        .describe("Mark at most one option per question as recommended."),
});
const InAppAgentQuestionnaireQuestionSchema = zod_1.default.object({
    question: zod_1.default.string().min(1).max(500),
    options: zod_1.default
        .array(InAppAgentQuestionnaireOptionSchema)
        .min(exports.IN_APP_AGENT_QUESTIONNAIRE_MIN_OPTIONS)
        .max(exports.IN_APP_AGENT_QUESTIONNAIRE_MAX_OPTIONS)
        .refine((options) => options.filter((option) => option.recommended).length <= 1, { message: "Mark at most one option per question as recommended." })
        .describe(`${exports.IN_APP_AGENT_QUESTIONNAIRE_MIN_OPTIONS} to ${exports.IN_APP_AGENT_QUESTIONNAIRE_MAX_OPTIONS} distinct answer options, each with a description. Do not add an "Other" option; the user can always type a custom answer.`),
    allowMultiple: zod_1.default
        .boolean()
        .describe("Whether the user may select more than one option."),
});
exports.InAppAgentQuestionnaireArgsSchema = zod_1.default.object({
    questions: zod_1.default
        .array(InAppAgentQuestionnaireQuestionSchema)
        .min(1)
        .max(exports.IN_APP_AGENT_QUESTIONNAIRE_MAX_QUESTIONS),
});
const InAppAgentQuestionnaireAnswerSchema = zod_1.default.object({
    selectedOptionIndexes: zod_1.default
        .array(zod_1.default.number().int().nonnegative())
        .max(exports.IN_APP_AGENT_QUESTIONNAIRE_MAX_OPTIONS)
        .refine((indexes) => new Set(indexes).size === indexes.length, {
        message: "Select each option at most once.",
    }),
    otherText: zod_1.default
        .string()
        .max(exports.IN_APP_AGENT_QUESTIONNAIRE_MAX_ANSWER_LENGTH)
        .nullable(),
});
/** One entry per question, in question order. An empty entry means skipped. */
exports.InAppAgentQuestionnaireAnswersSchema = zod_1.default
    .array(InAppAgentQuestionnaireAnswerSchema)
    .max(exports.IN_APP_AGENT_QUESTIONNAIRE_MAX_QUESTIONS);
exports.InAppAgentQuestionnaireResultSchema = zod_1.default.object({
    type: zod_1.default.literal("questionnaireAnswers"),
    answers: zod_1.default.array(zod_1.default.object({
        question: zod_1.default.string(),
        answer: zod_1.default.array(zod_1.default.string()),
        skipped: zod_1.default.boolean(),
    })),
});
/**
 * Answers schema bound to the questions the model asked. Parsing checks every
 * answer against its question and outputs the tool result the model reads.
 */
function createInAppAgentQuestionnaireAnswersSchema(questions) {
    return exports.InAppAgentQuestionnaireAnswersSchema.length(questions.length, {
        message: "Expected exactly one answer entry per question.",
    })
        .superRefine((answers, ctx) => {
        if (answers.length !== questions.length) {
            return;
        }
        for (const [index, question] of questions.entries()) {
            if (!isValidAnswer(question, answers[index])) {
                ctx.addIssue({
                    code: "custom",
                    path: [index],
                    message: `Invalid answer for question ${index + 1}.`,
                });
            }
        }
    })
        .transform((answers) => ({
        type: "questionnaireAnswers",
        answers: questions.map((question, index) => {
            const answer = toAnswerLabels(question, answers[index]);
            return {
                question: question.question,
                answer,
                skipped: answer.length === 0,
            };
        }),
    }));
}
/**
 * Validates user answers against the questions the model asked and turns
 * them into the tool result the model reads. Shared by the decision endpoint
 * (to reject bad input early) and the worker tool execution.
 */
function resolveInAppAgentQuestionnaireAnswers({ args, answers, }) {
    const parsedArgs = exports.InAppAgentQuestionnaireArgsSchema.safeParse(args);
    if (!parsedArgs.success) {
        return { success: false, error: "Invalid questionnaire questions." };
    }
    const parsedAnswers = createInAppAgentQuestionnaireAnswersSchema(parsedArgs.data.questions).safeParse(answers);
    if (!parsedAnswers.success) {
        return {
            success: false,
            error: parsedAnswers.error.issues[0]?.message ??
                "Invalid questionnaire answers.",
        };
    }
    return { success: true, result: parsedAnswers.data };
}
function getOtherText(answer) {
    return answer.otherText?.trim() || null;
}
function isValidAnswer(question, answer) {
    const { selectedOptionIndexes } = answer;
    const isOutOfRange = selectedOptionIndexes.some((optionIndex) => optionIndex >= question.options.length);
    if (isOutOfRange) {
        return false;
    }
    const answerCount = selectedOptionIndexes.length + (getOtherText(answer) ? 1 : 0);
    return question.allowMultiple || answerCount <= 1;
}
function toAnswerLabels(question, answer) {
    const labels = answer.selectedOptionIndexes.map((optionIndex) => question.options[optionIndex].label);
    const otherText = getOtherText(answer);
    return otherText === null ? labels : [...labels, otherText];
}
